import XRegExpUnicodeBase from 'xregexp/lib/addons/unicode-base';
import XRegExpUnicodeCategories from 'xregexp/lib/addons/unicode-categories';
import XRegExp from 'xregexp/lib/xregexp';

export class DomainNameValidator {
  private readonly domainName: string;

  private readonly hasConsequentDots = (): boolean => /.*\.{2,}.*/g.test(this.domainName);

  private readonly hasBadTLD = (): boolean => {
    const tldPartRegexp = /.*\.(\S{2,})$/;
    const tldMatches = this.domainName.match(tldPartRegexp);

    if (tldMatches === null) {
      return true;
    }

    const tld = tldMatches[1];

    const isPunyCodeRegexp = /^xn--\S/i; // i - for case insensitive

    if (isPunyCodeRegexp.test(tld)) {
      return false;
    }

    const hasNumbersRegexp = /[0-9]+/;

    return hasNumbersRegexp.test(tld);
  };

  private readonly isPartToLong = (): boolean => {
    const MAX_DOMAIN_NAME_PART_LENGTH = 63;
    const domainNameParts = this.domainName.split('.');
    if (domainNameParts.length > 1) {
      domainNameParts.pop();
    }

    return domainNameParts.some((domainPart) => domainPart.length > MAX_DOMAIN_NAME_PART_LENGTH);
  };

  private readonly hasInvalidCharacters = (): boolean => {
    const hasSpaceInside = () => /^\S+.*(\s+).*\S+$/g.test(this.domainName);
    const hasOnlyLettersOrNumbers = () => XRegExp('^(\\pL|\\pS|[0-9-])+$').test(this.domainName) === true;
    const hasCommonSpecialCharacters = () => XRegExp('[~!#$%^&*()+{}|:\',"/@?]+').test(this.domainName) === true;
    return hasSpaceInside() || (hasOnlyLettersOrNumbers() === false && hasCommonSpecialCharacters());
  };

  private readonly isToLong = () => {
    const MAX_DOMAIN_NAME_LENGTH = 255;
    return this.domainName.length > MAX_DOMAIN_NAME_LENGTH;
  };

  private readonly isToShort = () => {
    const MIN_DOMAIN_NAME_LENGTH = 5;
    return this.domainName.length < MIN_DOMAIN_NAME_LENGTH;
  };

  private readonly hasBadHyphens = () => {
    return this.domainName.split('.').some((domainPart) => /(^-.*)|(.*-$)/.test(domainPart));
  };

  private readonly hasEmailResemblance = (): boolean => {
    return /@/.test(this.domainName);
  };

  constructor(domainName: string) {
    this.domainName = domainName;
    XRegExpUnicodeBase(XRegExp);
    XRegExpUnicodeCategories(XRegExp);
  }

  validate = (): boolean => {
    const validationFunctions: (() => boolean)[] = [
      this.hasConsequentDots,
      this.hasEmailResemblance,
      this.hasInvalidCharacters,
      this.isToLong,
      this.isPartToLong,
      this.isToShort,
      this.hasBadHyphens,
      this.hasBadTLD,
    ];

    return !validationFunctions.some((func) => func());
  };
}
